<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020-2023 (original work) Open Assessment Technologies SA ;
    import { IconBarButton } from '@oat-sa-private/ui-elements';
    import { createEventDispatcher } from 'svelte';
    import { breakpoints } from '@oat-sa-private/ui-identity';

    const dispatch = createEventDispatcher();

    export let logoSrc = '';
    export let logoLinkUrl = '';

    export let logoAltText = 'logo';
    export let startActions = [];
    export let endActions = [];
    export let maxEndActions = endActions.length; /* TODO: submenu responds to MQ ? */
    export let iconSize;

    if (maxEndActions > endActions.length || maxEndActions < 0) {
        maxEndActions = endActions.length;
    }

    $: visibleEndActions = endActions.slice(0, maxEndActions);
    $: hiddenEndActions = endActions.slice(maxEndActions);

    // Choose the button size based on window width
    let iconSizeResult;
    let windowWidth;
    $: if (!iconSize) {
        iconSizeResult = windowWidth <= breakpoints.width.medium ? '16' : '24';
    } else {
        iconSizeResult = iconSize;
    }

    let hiddenActionsExpanded = false;

    function toggleActionSubmenu(e) {
        hiddenActionsExpanded = !hiddenActionsExpanded;
        e.preventDefault();
        e.stopPropagation();
    }

    function closeActionSubmenu() {
        hiddenActionsExpanded = false;
    }

    function emitAction(key) {
        dispatch('action', { key });
    }
</script>

<style>
    .headerbar {
        --headerbar-default-height: 6rem;
        --headerbar-default-background: var(--color-bg-default);
        --headerbar-default-side-padding: var(--space-quarter);

        display: flex;
        flex-wrap: nowrap;
        align-items: center;
        width: 100%;
        height: var(--headerbar-height, var(--headerbar-default-height));
        background: var(--headerbar-background, var(--headerbar-default-background));
        padding: 0 var(--headerbar-side-padding, var(--headerbar-default-side-padding));

        & ol {
            list-style: none;
            padding: 0;
            margin: 0;
        }
        & header {
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
            flex: 1 1 0;
            min-width: 0;
            height: 100%;
        }
        & .start,
        & .logo,
        & .content,
        & .end {
            height: 100%;
            display: flex;
            flex-wrap: nowrap;
            align-items: center;
        }
        & .logo {
            margin-block: 0;
            margin-inline: 0 var(--space-threequarters);
            max-width: 7rem;
            min-width: 4rem;
            & a {
                height: 100%;
            }
            & img {
                height: 100%;
                max-width: 100%;
                object-fit: contain;
            }
        }
        & .content {
            flex-grow: 1;
            white-space: nowrap;
            overflow: hidden; /* TODO: ellipsis */
            & > :global(*) {
                /* slot children (esp. headings) may have unwanted margin: remove it */
                margin: 0;
            }
        }
        & .start {
            margin-block: 0;
            margin-inline: 0 var(--space-threequarters);
        }
        & .end {
            margin-block: 0;
            margin-inline: var(--space-threequarters) 0;

            /* Toggler and hidden action menu */
            & > li:last-child {
                position: relative;
                & ol {
                    position: absolute;
                    top: 100%;
                    inset-inline-end: 0;
                    flex-direction: column;
                }
            }
        }
    }
    /* larger than mobile */
    @media screen and (--mq-minwidth-medium) {
        .headerbar {
            --headerbar-default-height: 8rem;
            --headerbar-default-side-padding: var(--space-2x5);

            & .start {
                flex: 0;
                flex-basis: 6rem;
                justify-content: center;
                margin-block: 0;
                margin-inline: 0 var(--space-1x5);

                /* spacing between list items */
                & > li:not(:last-child) {
                    margin-block: 0;
                    margin-inline: 0 var(--space-1x);
                }
            }
            & .end {
                margin-block: 0;
                margin-inline: var(--space-1x5) 0;

                /* spacing between list items */
                & > li:not(:last-child) {
                    margin-block: 0;
                    margin-inline: 0 var(--space-1x);
                }
            }
            & .logo {
                margin-block: 0;
                margin-inline: 0 var(--space-1x5);
            }
        }
    }
    /* smaller than desktop */
    @media screen and (--mq-maxwidth-huge) {
        .headerbar {
            padding: 0 var(--space-1x);

            & .start {
                margin-block: 0;
                margin-inline: 0 var(--space-1x);
            }

            & .end {
                margin-block: 0;
                margin-inline: var(--space-1x) 0;
            }

            & .logo {
                display: none;
            }

            & header {
                /* here we handle special case if no start actions present but logo is */
                & :first-child {
                    &.logo {
                        margin: 0 var(--space-1x);
                        display: flex;
                    }
                }
            }
        }
    }
</style>

<svelte:window bind:innerWidth={windowWidth} on:click={closeActionSubmenu} />

<div class="headerbar">
    <header>
        {#if startActions && startActions.length}
            <ol class="start">
                {#each startActions as buttonProps}
                    <li>
                        <IconBarButton
                            {...buttonProps}
                            size={`base-${iconSizeResult}`}
                            icon={`${buttonProps.icon}-${iconSizeResult}`}
                            on:click={() => emitAction(buttonProps.key)} />
                    </li>
                {/each}
            </ol>
        {/if}
        {#if logoSrc}
            <div class="logo">
                {#if logoLinkUrl}
                    <!-- svelte-ignore security-anchor-rel-noreferrer -->
                    <a href={logoLinkUrl} target="_blank"> <img src={logoSrc} alt={logoAltText} /> </a>
                {:else}<img src={logoSrc} alt={logoAltText} />{/if}
            </div>
        {/if}
        <div class="content">
            <slot />
        </div>
    </header>
    <aside>
        <ol class="end">
            {#each visibleEndActions as buttonProps}
                <li>
                    <IconBarButton
                        {...buttonProps}
                        size={`base-${iconSizeResult}`}
                        icon={`${buttonProps.icon}-${iconSizeResult}`}
                        on:click={() => emitAction(buttonProps.key)} />
                </li>
            {/each}
            {#if hiddenEndActions.length}
                <li>
                    <IconBarButton
                        label="More"
                        ariaLabel={false}
                        size={`base-${iconSizeResult}`}
                        icon={`more-${iconSizeResult}`}
                        on:click={toggleActionSubmenu} />
                    {#if hiddenActionsExpanded}
                        <ol>
                            {#each hiddenEndActions as buttonProps}
                                <li>
                                    <IconBarButton
                                        {...buttonProps}
                                        size={`base-${iconSizeResult}`}
                                        icon={`${buttonProps.icon}-${iconSizeResult}`}
                                        on:click={() => emitAction(buttonProps.key)} />
                                </li>
                            {/each}
                        </ol>
                    {/if}
                </li>
            {/if}
        </ol>
        <slot name="aside-after" />
    </aside>
</div>
